import React from "react";
import { Text, View, StyleSheet, Svg, Circle } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { userHasSomePermissions } from "../permissionsUtils";
import { textBlueColor, blue, green, yellow, orange, red } from "./pdfCssStyles";
import { PermissionIdEnum } from "../types/permissions/permissionIdEnum";

const styles = StyleSheet.create({
  table: {
    display: "table",
    width: "auto",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5, // Bottom border for rows
    borderStyle: "solid",
    borderColor: "#000",
  },
  tableCol: {
    flex: 1,
    borderRightWidth: 0.5, // Right border for all columns
    borderStyle: "solid",
    borderColor: "#000",
  },
  flex5TableCol: {
    flex: 5,
    borderRightWidth: 0.5,
    borderLeftWidth: 0.5,
    borderStyle: "solid",
    borderColor: "#000",
  },
  flex2TableCol: {
    flex: 2,
    borderRightWidth: 0.5,
    borderStyle: "solid",
    borderColor: "#000",
  },
  tableCell: {
    margin: 3,
    height: "14px",
    fontSize: 10,
  },
  header: {
    backgroundColor: "#eee",
    color: textBlueColor,
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTopWidth: 0.5, // Top border for header
    borderStyle: "solid",
    borderColor: "#000",
  },
  fontSize8: {
    fontSize: 8,
  },
});

const noteStyles = StyleSheet.create({
  noteContainer: {
    marginTop: 1, // Space above the note
    marginBottom: 5, // Space above the note
    marginLeft: 10, // Indent from the left
    padding: 1, // Padding inside the container
  },
  note: {
    fontSize: 7, // Smaller font for the note
    textAlign: "left", // Align text to the left
    marginLeft: 10, // Indent from the left
  },
  bold: {
    fontFamily: "Helvetica-Bold", // Use a bold font
  },
});

const getRowColor = (riskRow) => {
  const riskPercentage = (riskRow.rating / riskRow.maxRateAccepted) * 100;

  if (riskPercentage === 0) return "white";
  if (riskPercentage <= 20) return blue;
  if (riskPercentage <= 40) return green;
  if (riskPercentage <= 60) return yellow;
  if (riskPercentage <= 80) return orange;
  if (riskPercentage <= 100) return red;
  return "white";
};

export const RiskEvaluationTable = ({ tableRisksData }) => {
  const { t } = useTranslation();

  return (
    <View style={styles.table} wrap={false}>
      {/* columns */}
      <View style={[styles.tableRow, styles.header]}>
        <View style={styles.flex5TableCol}>
          <Text style={[styles.tableCell, styles.fontSize8]}>{`${t("pdfFile.perilName")}`}</Text>
        </View>

        <View style={styles.tableCol}>
          <Text style={[styles.tableCell, styles.fontSize8]}>{`${t("pdfFile.rating")}`}</Text>
        </View>

        <View style={styles.tableCol}>
          <Text style={[styles.tableCell, styles.fontSize8]}>{`${t("pdfFile.outOf")}`}</Text>
        </View>

        <View style={styles.flex2TableCol}>
          <Text style={[styles.tableCell, styles.fontSize8]}>{`${t("pdfFile.hazardLevel")}`}</Text>
        </View>

        <View style={styles.tableCol}>
          <Text style={styles.tableCell}></Text>
        </View>
      </View>

      {/* rows */}
      {tableRisksData?.map(
        (risk, rowIndex) =>
          userHasSomePermissions([...risk.permissions, PermissionIdEnum.Super]) && (
            <View style={styles.tableRow} wrap={false} key={rowIndex}>
              <View style={styles.flex5TableCol}>
                <Text style={styles.tableCell}>{risk.label}</Text>
                {risk.validityNote && risk.validityNote !== "" && (
                  <Text style={noteStyles.note}>{risk.validityNote}</Text>
                )}
              </View>

              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{risk.rating}</Text>
              </View>

              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{risk.maxRateAccepted - 1}</Text>
              </View>

              <View style={styles.flex2TableCol}>
                <Text style={styles.tableCell}>{risk.level}</Text>
              </View>

              <View style={styles.tableCol}>
                <View style={[styles.tableCell]}>
                  <Svg>
                    <Circle cx="8" cy="8" r="6" stroke="black" strokeWidth="0.5" fill={getRowColor(risk)} />
                  </Svg>
                </View>
              </View>
            </View>
          ),
      )}
    </View>
  );
};
